import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Customer, CustomerStateTypes } from "./Customer.util";

const name = "customers";
const extraActions = createExtraActions();

const CustomerInitialState: CustomerStateTypes = {
  createCustomer: ResetApiState(null),
  resetCreateCustomer: ResetApiState(null),
  editCustomer: ResetApiState(null),
  resetEditCustomer: ResetApiState(null),
  deleteCustomer: ResetApiState(null),
  resetDeleteCustomer: ResetApiState(null),
  customers: ResetApiState([]),
  pagedCustomers: ResetApiState(ResetPaginationState()),
  customer: ResetApiState(null),
  resetCustomer: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createCustomer = createAsyncThunk<ResponseWrapper<Customer>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateCustomer = createAction(`/reset-create-${name}`);

  const editCustomer = createAsyncThunk<ResponseWrapper<Customer>, any>(
    `/edit-${name}`,
    async ({ id, status }: any, thunkApi) =>
      axios
        .post(`/${name}/${id}/status/${status}`)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditCustomer = createAction(`/reset-edit-${name}`);

  const deleteCustomer = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteCustomer = createAction(`/reset-delete-${name}`);

  const getCustomers = createAsyncThunk<Customer[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedCustomers = createAsyncThunk<PaginationType<Customer[]>, any>(
    `/get-paged-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/paged${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getCustomer = createAsyncThunk<Customer, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCustomer = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createCustomer,
    resetCreateCustomer,
    editCustomer,
    resetEditCustomer,
    deleteCustomer,
    resetDeleteCustomer,
    getCustomers,
    getPagedCustomers,
    getCustomer,
    resetCustomer,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<CustomerStateTypes>
) {
  return {
    ...createCustomerReducer(),
    ...resetCreateCustomer(),
    ...editCustomerReducer(),
    ...resetEditCustomer(),
    ...deleteCustomerReducer(),
    ...resetDeleteCustomer(),
    ...customersReducer(),
    ...pagedCustomersReducer(),
    ...customerReducer(),
    ...resetCustomer(),
    ...resetStatesReducer(),
  };

  function createCustomerReducer() {
    return {
      ...builder.addCase(
        extraActions.createCustomer.pending,
        (state, action) => {
          state.createCustomer = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createCustomer.fulfilled,
        (state, action) => {
          state.createCustomer = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createCustomer.rejected,
        (state, action) => {
          state.createCustomer = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateCustomer() {
    return {
      ...builder.addCase(extraActions.resetCreateCustomer, (state, action) => {
        state.resetCreateCustomer = ResetApiState(true);
        state.createCustomer = ResetApiState(null);
      }),
    };
  }

  function editCustomerReducer() {
    return {
      ...builder.addCase(extraActions.editCustomer.pending, (state, action) => {
        state.editCustomer = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.editCustomer.fulfilled,
        (state, action) => {
          state.editCustomer = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editCustomer.rejected,
        (state, action) => {
          state.editCustomer = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetEditCustomer() {
    return {
      ...builder.addCase(extraActions.resetEditCustomer, (state, action) => {
        state.resetEditCustomer = ResetApiState(true);
        state.editCustomer = ResetApiState(null);
      }),
    };
  }

  function deleteCustomerReducer() {
    return {
      ...builder.addCase(
        extraActions.deleteCustomer.pending,
        (state, action) => {
          state.deleteCustomer = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteCustomer.fulfilled,
        (state, action) => {
          state.deleteCustomer = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteCustomer.rejected,
        (state, action) => {
          state.deleteCustomer = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteCustomer() {
    return {
      ...builder.addCase(extraActions.resetDeleteCustomer, (state, action) => {
        state.resetDeleteCustomer = ResetApiState(true);
        state.deleteCustomer = ResetApiState(null);
      }),
    };
  }

  function customersReducer() {
    return {
      ...builder.addCase(extraActions.getCustomers.pending, (state, action) => {
        state.customers = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getCustomers.fulfilled,
        (state, action) => {
          state.customers = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCustomers.rejected,
        (state, action) => {
          state.customers = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function pagedCustomersReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedCustomers.pending,
        (state, action) => {
          state.pagedCustomers = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedCustomers.fulfilled,
        (state, action) => {
          state.pagedCustomers = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedCustomers.rejected,
        (state, action) => {
          state.pagedCustomers = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function customerReducer() {
    return {
      ...builder.addCase(extraActions.getCustomer.pending, (state, action) => {
        state.customer = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getCustomer.fulfilled,
        (state, action) => {
          state.customer = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(extraActions.getCustomer.rejected, (state, action) => {
        state.customer = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetCustomer() {
    return {
      ...builder.addCase(extraActions.resetCustomer, (state, action) => {
        state.resetCustomer = ResetApiState(true);
        state.customer = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createCustomer = ResetApiState(null);
        state.resetCreateCustomer = ResetApiState(null);
        state.editCustomer = ResetApiState(null);
        state.resetEditCustomer = ResetApiState(null);
        state.deleteCustomer = ResetApiState(null);
        state.resetDeleteCustomer = ResetApiState(null);
        state.customers = ResetApiState([]);
        state.customer = ResetApiState(null);
        state.resetCustomer = ResetApiState(null);
        state.resetCustomer = ResetApiState(null);
      }),
    };
  }
}

const CustomerSlice = createSlice({
  name,
  initialState: CustomerInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const CustomerActions = {
  ...CustomerSlice.actions,
  ...extraActions,
};
export const CustomerReducer = CustomerSlice.reducer;
